import { Bounce, toast, ToastOptions } from 'react-toastify';
import { apiClient } from './httpClient';

export const rowsPerPage = 5;
export const tableRowHeight = 65;
export const fixedTableScrollY = rowsPerPage * tableRowHeight;

export const userRoles = [
  { key: 'EMPLOYEE', text: 'Employee', value: 'EMPLOYEE' },
  { key: 'ADMIN', text: 'Admin', value: 'ADMIN' },
  { key: 'MANAGER', text: 'Manager', value: 'MANAGER' },
  { key: 'HR', text: 'HR', value: 'HR' },
  { key: 'TIMEKEEPER', text: 'Timekeeper', value: 'TIMEKEEPER' },
];

export const employeeTypes = [
  { key: 'CIM', text: 'CIM', value: 'CIM' },
  { key: 'COLABORATOR', text: 'Colaborator', value: 'COLABORATOR' },
];

export const userStatuses = [
  { key: 'ACTIVE', text: 'Active', value: 'ACTIVE' },
  { key: 'INACTIVE', text: 'Inactive', value: 'INACTIVE' },
];

export const filters = [
  { name: 'Approved' },
  { name: 'Rejected' },
  { name: 'Cancelled' },
  { name: 'Pending' },
  { name: 'PendingCancelled' },
];

export const filterStatusMap: Record<string, string> = {
  Approved: 'APPROVED',
  Rejected: 'REJECTED',
  Cancelled: 'CANCELLED',
  Pending: 'AWAITING_MANAGER',
  PendingCancelled: 'AWAITING_MANAGER_CANCELLATION',
};

export const isBooleanDropdown = [
  { key: 'yes', text: 'Yes', value: true },
  { key: 'no', text: 'No', value: false },
];

export enum RequestStatus {
  APPROVE = 'APPROVE',
  REJECT = 'REJECT',
  CANCEL = 'CANCEL',
  AWAITING_MANAGER = 'AWAITING_MANAGER',
}

export const fetchData = async <T>(
  api: string,
  setData: (data: T) => void,
  delay = 0, // mo delay neded in general, why is this here?, idk
) => {
  return new Promise<void>((resolve) => {
    setTimeout(async () => {
      try {
        const response = await apiClient.get(api);

        setData(response.data);
      } catch (error) {
        console.error(`Error fetching ${api}:`, error);
        
      } finally {
        resolve();
      }
    }, delay);
  });
};

export const postData = async (api: string, formData: unknown) => {
  await apiClient.post(api, formData);
};

export const updateData = async (api: string, formData: unknown) => {
  await apiClient.put(api, formData);
};

export const Notify = (
  type: string,
  message: string,
  onNotify?: () => void,
  toastId?: string,
) => {
  const options: ToastOptions = {
    position: 'bottom-right',
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: 'colored',
    transition: Bounce,
    toastId,
    onClick: onNotify,
  };

  switch (type) {
    case 'success':
      toast.success(message, options);
      break;
    case 'error':
      toast.error(message, options);
      break;
    case 'info':
      toast.info(message, options);
      break;
    default:
      return;
  }
};

export const formatEnumLabel = (input: string): string => {
  return input
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const getRequestStatusType = (type: string): string => {
  switch (type) {
    case 'APPROVED':
      return 'request-approved';
    case 'REJECTED':
      return 'request-rejected';
    case 'AWAITING_MANAGER':
    case 'AWAITING_ADMINISTRATIV':
    case 'RETURNED_FOR_CORRECTION':
    case 'AWAITING_DOCUMENT_REVIEW':
      return 'request-pending';
    case 'AWAITING_MANAGER_CANCELLATION':
    case 'AWAITING_ADMINISTRATIV_CANCELLATION':
      return 'request-cancel-pending';
    case 'CANCELLED':
      return 'request-cancelled';
    default:
      return '';
  }
};

export const handleRowDelete = async (
  name: string,
  api: string,
  item: { id?: string | number },
  refreshData: () => void | Promise<void>,
) => {
  try {
    await apiClient.delete(`${api}/${item?.id}`);

    Notify('success', `${name} deleted successfully!`);
    await refreshData();
  } catch (error) {
    console.error('DELETE error:', error);
    Notify(
      'error',
      getErrorMessage(error, 'Oops! Something went wrong while updating.'),
    );
  }
};

export const countWorkdays = (startDate: Date, endDate: Date): number => {
  let count = 0;
  const current = new Date(startDate);

  current.setHours(0, 0, 0, 0);
  endDate = new Date(endDate);
  endDate.setHours(0, 0, 0, 0);

  while (current <= endDate) {
    const day = current.getDay();
    if (day !== 0 && day !== 6) {
      count++;
    }
    current.setDate(current.getDate() + 1);
  }

  return count;
};

export const hasRoleAccess = (roles: string[], allowed: string[]) => {
  const hasAccess = allowed.some((role) => roles.includes(`ROLE_${role}`));
  return hasAccess;
};

const isLeapYear = (year: number): boolean => {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
};

export const months = (year: number) => [
  { name: 'January', days: 31 },
  {
    name: 'February',
    days: isLeapYear(year) ? 29 : 28,
  },
  { name: 'March', days: 31 },
  { name: 'April', days: 30 },
  { name: 'May', days: 31 },
  { name: 'June', days: 30 },
  { name: 'July', days: 31 },
  { name: 'August', days: 31 },
  { name: 'September', days: 30 },
  { name: 'October', days: 31 },
  { name: 'November', days: 30 },
  { name: 'December', days: 31 },
];

export const getYears = () => {
  const thisYear = new Date().getFullYear();
  const lastYear = thisYear - 1;

  return [
    {
      key: String(lastYear),
      text: String(lastYear),
      value: String(lastYear),
    },
    {
      key: String(thisYear),
      text: String(thisYear),
      value: String(thisYear),
    },
  ];
};

export const toDropdownListFormat = (
  data: Record<string, unknown>[],
  nameKey = 'name',
  idKey = 'id',
): { key: string; text: string; value: string | number }[] => {
  return data.map((item) => {
    const name = String(item[nameKey] ?? '');
    const value = (item[idKey] as string | number | undefined) ?? name;

    return {
      key: name.replace(/\s+/g, ''),
      text: name,
      value: value,
    };
  });
};

type ErrorLike = {
  response?: { data?: { message?: string } };
  message?: string;
};

export const getErrorMessage = (
  error: unknown,
  defaultErrorMsg = 'Oops! Something went wrong.',
) => {
  const err = error as ErrorLike;
  return err?.response?.data?.message || err?.message || defaultErrorMsg;
};
